import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Botao, MenuDeAcoes, useDialogo } from '../../componentes/ui';
import {
  criarColuna,
  editarColuna,
  excluirColuna,
  moverCard,
  type AutomacaoAplicada,
  type CardResumo,
  type ColunaDoQuadro,
  type Quadro,
  type TagDoCrm,
} from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';
import { CardDoPipeline } from './CardDoPipeline';

/** Coluna → ids dos cards, na ordem da tela. */
type Arranjo = Readonly<Record<string, readonly string[]>>;

function arranjoDe(quadro: Quadro): Arranjo {
  return Object.fromEntries(quadro.colunas.map((c) => [c.id, c.cards.map((card) => card.id)]));
}

/**
 * O pipeline em colunas, com os cards arrastáveis entre elas.
 *
 * ## O arrastar é local até soltar
 *
 * Durante o arrasto o quadro se rearranja só na tela (`arranjo`), para o card
 * abrir espaço onde vai cair. Ao soltar, uma chamada só grava coluna e posição;
 * se o servidor recusar, a tela volta ao que ele tem. Gravar a cada coluna
 * sobrevoada mandaria dezenas de requisições por arrasto.
 *
 * ## Clique abre, arrasto move
 *
 * No mouse, o arrasto só começa depois de o ponteiro andar alguns pixels: um
 * clique parado no título abre o card. No toque, segurar um instante levanta o
 * card, e deslizar sem segurar continua rolando o quadro. Pelo teclado, espaço levanta o card, as setas o levam e espaço de novo o solta.
 */
export function QuadroKanban(props: {
  readonly quadro: Quadro;
  readonly aoAbrirCard: (id: string) => void;
  readonly aoAdicionarNaColuna: (colunaId: string) => void;
  /** O card solto numa etapa com automação levou a empresa a outro quadro. */
  readonly aoAutomatizar?: (aplicada: AutomacaoAplicada, empresa: string) => void;
  /**
   * Com a busca do pipeline ligada as colunas mostram só parte dos cards, e a
   * posição de soltar sairia contada errada: arrastar fica desligado.
   */
  readonly semArrastar?: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const [arranjo, setArranjo] = useState<Arranjo>(() => arranjoDe(props.quadro));
  const [ativo, setAtivo] = useState<string | null>(null);
  const origem = useRef<{ colunaId: string; indice: number } | null>(null);

  /*
    Só uma resposta **nova** do servidor substitui o arranjo, e nunca no meio
    de um arrasto — apagaria o espaço que o card está abrindo. O fim do arrasto
    não dispara nada aqui: se disparasse, o card voltaria por um instante ao
    lugar antigo (o quadro ainda é o de antes da gravação) até a resposta chegar.
  */
  useEffect(() => {
    if (origem.current === null) setArranjo(arranjoDe(props.quadro));
  }, [props.quadro]);

  const cards = useMemo(
    () =>
      new Map<string, CardResumo>(
        props.quadro.colunas.flatMap((c) => c.cards.map((card) => [card.id, card] as const)),
      ),
    [props.quadro],
  );
  const tags = useMemo(
    () => new Map<string, TagDoCrm>(props.quadro.tags.map((t) => [t.id, t])),
    [props.quadro.tags],
  );
  const nomesDasColunas = useMemo(
    () => new Map(props.quadro.colunas.map((c) => [c.id, c.nome])),
    [props.quadro.colunas],
  );

  const recarregar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['crm'] });
  };

  const mover = useMutation({
    mutationFn: (m: { id: string; colunaId: string; indice: number }) =>
      moverCard(m.id, m.colunaId, m.indice),
    onSuccess: (r, m) => {
      if (r.automacao !== null) {
        props.aoAutomatizar?.(r.automacao, cards.get(m.id)?.razaoSocial ?? 'A empresa');
      }
    },
    onError: () => setArranjo(arranjoDe(props.quadro)),
    onSettled: recarregar,
  });
  const alterarColuna = useMutation({
    mutationFn: (m: { id: string; nome?: string; ordem?: number }) =>
      editarColuna(m.id, {
        ...(m.nome === undefined ? {} : { nome: m.nome }),
        ...(m.ordem === undefined ? {} : { ordem: m.ordem }),
      }),
    onSettled: recarregar,
  });
  const apagarColuna = useMutation({ mutationFn: excluirColuna, onSettled: recarregar });
  const novaColuna = useMutation({
    mutationFn: (nome: string) => criarColuna(props.quadro.id, nome),
    onSettled: recarregar,
  });

  const sensores = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // No toque, segurar um instante levanta o card; deslizar sem segurar rola.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const colunaDe = (id: string, arr: Arranjo = arranjo): string | undefined =>
    id in arr ? id : Object.keys(arr).find((c) => arr[c]?.includes(id));

  const aoComecar = (evento: DragStartEvent): void => {
    const id = String(evento.active.id);
    const coluna = colunaDe(id);
    if (coluna === undefined) return;
    setAtivo(id);
    origem.current = { colunaId: coluna, indice: arranjo[coluna]?.indexOf(id) ?? 0 };
  };

  /** Atravessar para outra coluna: o card sai de uma e abre espaço na outra. */
  const aoPassar = (evento: DragOverEvent): void => {
    const { active, over } = evento;
    if (over === null) return;
    const id = String(active.id);
    const sobre = String(over.id);

    setArranjo((atual) => {
      const de = colunaDe(id, atual);
      const para = colunaDe(sobre, atual);
      if (de === undefined || para === undefined || de === para) return atual;

      const destino = atual[para] ?? [];
      const indiceSobre = destino.indexOf(sobre);
      const retangulo = active.rect.current.translated;
      const abaixo = retangulo !== null && retangulo.top > over.rect.top + over.rect.height / 2;
      // Sobre a coluna em si (vazia, ou o espaço abaixo dos cards): vai para o fim.
      const indice = indiceSobre < 0 ? destino.length : indiceSobre + (abaixo ? 1 : 0);

      return {
        ...atual,
        [de]: (atual[de] ?? []).filter((x) => x !== id),
        [para]: [...destino.slice(0, indice), id, ...destino.slice(indice)],
      };
    });
  };

  const aoSoltar = (evento: DragEndEvent): void => {
    const id = String(evento.active.id);
    const inicio = origem.current;
    setAtivo(null);
    origem.current = null;

    if (evento.over === null || inicio === null) {
      setArranjo(arranjoDe(props.quadro));
      return;
    }

    const coluna = colunaDe(id);
    if (coluna === undefined) return;
    let lista = arranjo[coluna] ?? [];
    const de = lista.indexOf(id);
    const para = lista.indexOf(String(evento.over.id));
    if (para >= 0 && de !== para) {
      lista = arrayMove([...lista], de, para);
      setArranjo({ ...arranjo, [coluna]: lista });
    }

    const indice = lista.indexOf(id);
    if (inicio.colunaId !== coluna || inicio.indice !== indice) {
      mover.mutate({ id, colunaId: coluna, indice });
    }
  };

  const anuncios: Announcements = {
    onDragStart: ({ active }) => `${nomeDoCard(cards, active.id)} levantado.`,
    onDragOver: ({ active, over }) =>
      over === null
        ? undefined
        : `${nomeDoCard(cards, active.id)} sobre ${
            nomesDasColunas.get(String(over.id)) ?? nomeDoCard(cards, over.id)
          }.`,
    onDragEnd: ({ active, over }) =>
      over === null
        ? `${nomeDoCard(cards, active.id)} solto fora do quadro. Nada mudou.`
        : `${nomeDoCard(cards, active.id)} solto.`,
    onDragCancel: ({ active }) => `Arrasto cancelado. ${nomeDoCard(cards, active.id)} voltou.`,
  };

  const erro = mover.error ?? alterarColuna.error ?? apagarColuna.error ?? novaColuna.error;
  const cardAtivo = ativo === null ? undefined : cards.get(ativo);
  const colunas = props.quadro.colunas;

  return (
    <div className="flex flex-col gap-4">
      {erro !== null && <ErroDaApi titulo="Não foi possível salvar no pipeline" erro={erro} />}

      <DndContext
        sensors={props.semArrastar === true ? [] : sensores}
        collisionDetection={closestCorners}
        onDragStart={aoComecar}
        onDragOver={aoPassar}
        onDragEnd={aoSoltar}
        onDragCancel={() => {
          setAtivo(null);
          origem.current = null;
          setArranjo(arranjoDe(props.quadro));
        }}
        accessibility={{
          announcements: anuncios,
          screenReaderInstructions: {
            draggable:
              'Para mover um card, aperte espaço para levantá-lo, use as setas para levá-lo e espaço de novo para soltar. Esc cancela.',
          },
        }}
      >
        {/* A rolagem horizontal é do quadro, não da página. */}
        <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:-mx-8 sm:px-8">
          <div className="flex items-start gap-4">
            {colunas.map((coluna, i) => (
              <ColunaDoKanban
                key={coluna.id}
                coluna={coluna}
                cardIds={arranjo[coluna.id] ?? []}
                cards={cards}
                tags={tags}
                primeira={i === 0}
                ultima={i === colunas.length - 1}
                aoAbrirCard={props.aoAbrirCard}
                aoAdicionar={() => props.aoAdicionarNaColuna(coluna.id)}
                aoRenomear={async () => {
                  const nome = await dialogo.pedirTexto({
                    titulo: 'Renomear coluna',
                    rotulo: 'Nome da coluna',
                    valorInicial: coluna.nome,
                    obrigatorio: true,
                    rotuloConfirmar: 'Renomear',
                  });
                  if (nome !== null && nome !== coluna.nome) {
                    alterarColuna.mutate({ id: coluna.id, nome });
                  }
                }}
                aoMover={(delta) => alterarColuna.mutate({ id: coluna.id, ordem: i + delta })}
                aoExcluir={async () => {
                  const ok = await dialogo.confirmar({
                    titulo: `Excluir a coluna "${coluna.nome}"?`,
                    descricao: 'A coluna está vazia; os demais cards não mudam de lugar.',
                    rotuloConfirmar: 'Excluir',
                    tom: 'perigo',
                  });
                  if (ok) apagarColuna.mutate(coluna.id);
                }}
              />
            ))}

            <Botao
              variante="secundario"
              className="w-72 shrink-0"
              carregando={novaColuna.isPending}
              onClick={async () => {
                const nome = await dialogo.pedirTexto({
                  titulo: 'Nova coluna',
                  descricao: 'Ela entra no fim do quadro; dá para movê-la depois.',
                  rotulo: 'Nome da coluna',
                  obrigatorio: true,
                  rotuloConfirmar: 'Criar coluna',
                });
                if (nome !== null) novaColuna.mutate(nome);
              }}
            >
              + Nova coluna
            </Botao>
          </div>
        </div>

        <DragOverlay>
          {cardAtivo === undefined ? null : (
            <div className="w-72">
              <CardDoPipeline card={cardAtivo} tags={tags} flutuando />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function ColunaDoKanban(props: {
  readonly coluna: ColunaDoQuadro;
  readonly cardIds: readonly string[];
  readonly cards: ReadonlyMap<string, CardResumo>;
  readonly tags: ReadonlyMap<string, TagDoCrm>;
  readonly primeira: boolean;
  readonly ultima: boolean;
  readonly aoAbrirCard: (id: string) => void;
  readonly aoAdicionar: () => void;
  readonly aoRenomear: () => void;
  readonly aoMover: (delta: -1 | 1) => void;
  readonly aoExcluir: () => void;
}): JSX.Element {
  // A coluna inteira recebe cards — inclusive vazia, que não tem card para
  // servir de alvo.
  const { setNodeRef, isOver } = useDroppable({ id: props.coluna.id });
  const vazia = props.cardIds.length === 0;

  return (
    <section
      aria-label={`Coluna ${props.coluna.nome}`}
      className="flex max-h-[calc(100vh-15rem)] w-72 shrink-0 flex-col rounded-painel border border-borda bg-superficie-vidro/60"
    >
      <header className="flex items-center justify-between gap-2 px-3 pb-2 pt-3">
        <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-conteudo-primario">
          <span className="truncate">{props.coluna.nome}</span>
          <span className="rounded-full bg-superficie-base px-2 py-0.5 text-xs font-normal tabular-nums text-conteudo-sutil">
            {props.cardIds.length}
          </span>
        </h2>
        <MenuDeAcoes
          rotulo={`coluna ${props.coluna.nome}`}
          itens={[
            { rotulo: 'Renomear', aoEscolher: props.aoRenomear },
            {
              rotulo: 'Mover para a esquerda',
              desabilitado: props.primeira,
              aoEscolher: () => props.aoMover(-1),
            },
            {
              rotulo: 'Mover para a direita',
              desabilitado: props.ultima,
              aoEscolher: () => props.aoMover(1),
            },
            {
              rotulo: 'Excluir coluna',
              perigo: true,
              desabilitado: !vazia,
              descricao: vazia ? 'A coluna está vazia.' : 'Mova os cards antes de excluir.',
              aoEscolher: props.aoExcluir,
            },
          ]}
        />
      </header>

      <SortableContext items={[...props.cardIds]} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto px-3 pb-2 transition-colors ${
            isOver && vazia ? 'bg-acento-ciano/5' : ''
          }`}
        >
          {props.cardIds.map((id) => {
            const card = props.cards.get(id);
            return card === undefined ? null : (
              <CardArrastavel
                key={id}
                card={card}
                tags={props.tags}
                aoAbrir={() => props.aoAbrirCard(id)}
              />
            );
          })}
          {vazia && (
            <p className="rounded-card border border-dashed border-borda px-3 py-6 text-center text-xs text-conteudo-sutil">
              Arraste um card para cá
            </p>
          )}
        </div>
      </SortableContext>

      <div className="px-3 pb-3 pt-1">
        <Botao variante="sutil" tamanho="pequeno" className="w-full" onClick={props.aoAdicionar}>
          + Adicionar empresa
        </Botao>
      </div>
    </section>
  );
}

function CardArrastavel(props: {
  readonly card: CardResumo;
  readonly tags: ReadonlyMap<string, TagDoCrm>;
  readonly aoAbrir: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.card.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      // O que fica no lugar enquanto o original voa no `DragOverlay`.
      className={`cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40' : ''}`}
      {...attributes}
      {...listeners}
      aria-roledescription="card arrastável"
    >
      <CardDoPipeline card={props.card} tags={props.tags} aoAbrir={props.aoAbrir} />
    </div>
  );
}

function nomeDoCard(cards: ReadonlyMap<string, CardResumo>, id: string | number): string {
  const card = cards.get(String(id));
  return card === undefined ? 'Card' : card.nomeFantasia?.trim() || card.razaoSocial;
}
